import { useEffect, useRef, useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, type PagedResult } from "../../lib/api";
import { humanise, number } from "../../lib/format";
import { useResource } from "../../lib/hooks";
import { useSession } from "../../app/session";
import { useEventContext, type EventOption } from "../../app/eventContext";
import { Icon } from "../ui/Icon";
import { Avatar, ErrorState, IconButton, Modal, Pill, statusTone } from "../ui/primitives";
import { CommandPalette } from "./CommandPalette";
import { sectionForAnotherEvent } from "./workspacePath";
import "./TopBar.css";

/** The switcher entry that opens its search. It is never an event id. */
const FIND_EVENT = "find-event";

export function TopBar({ onToggleSidebar }: { onToggleSidebar: () => void }) {
  const { user, signOut } = useSession();
  const { events, hasMore, current, select } = useEventContext();
  const navigate = useNavigate();

  // Search, the assistant and alerts all work over internal records. Portal users
  // have their own navigation, and the API refuses them those routes anyway.
  const isExternal = Boolean(user?.isExternal);

  // Inside an event workspace the address names the event: the switcher shows that event, and choosing another
  // opens the same page of it rather than leaving the workspace on the old one.
  const workspace = useMatch("/events/:eventId/*");
  const workspaceEventId = workspace?.params.eventId ?? null;
  const workspaceSection = workspace?.params["*"] ? `/${workspace.params["*"]}` : "";
  const shownId = workspaceEventId ?? current?.id ?? "";
  const listed = !shownId || events.some((option) => option.id === shownId);

  // An event the switcher does not list, such as an archived one, is still shown while it is open.
  const workspaceEvent = useResource<{ id: string; code: string; name: string }>(
    ["event", workspaceEventId ?? ""],
    `/events/${workspaceEventId ?? ""}`,
    undefined,
    { enabled: Boolean(workspaceEventId) && !listed && !isExternal },
  );

  // Outside a workspace, so is an event chosen through the search from beyond the listed first page.
  const shownEvent = workspaceEventId ? workspaceEvent.data : current;
  const unlisted = !listed && shownEvent ? shownEvent : null;

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const chooseEvent = (next: string | null) => {
    select(next);

    if (workspaceEventId && next && next !== workspaceEventId) {
      navigate(`/events/${next}${sectionForAnotherEvent(workspaceSection)}`);
    }
  };

  // The unread count is real: it comes from the notifications the backend has
  // actually raised for this user, not a placeholder.
  const { data: unread } = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () => api.get<{ count: number }>("/notifications/unread-count"),
    refetchInterval: 60_000,
    enabled: Boolean(user) && !isExternal,
  });

  useEffect(() => {
    if (isExternal) return;

    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isExternal]);

  useEffect(() => {
    if (!menuOpen) return;

    const onClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };

    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [menuOpen]);

  return (
    <header className="topbar">
      {/* Shown once the sidebar becomes an overlay, which is the only way to reach it then. */}
      <IconButton label="Menu" className="iconbtn topbar__menu" onClick={onToggleSidebar}>
        <Icon.list width={17} height={17} />
      </IconButton>

      {!isExternal && (
        <div className="topbar__search">
          <Icon.search />
          <input
            readOnly
            value=""
            placeholder="Search events, people, partners, insights…"
            onFocus={() => setPaletteOpen(true)}
            onClick={() => setPaletteOpen(true)}
            aria-label="Search"
          />
          <span className="topbar__kbd">⌘K</span>
        </div>
      )}

      {!isExternal && (
        <IconButton
          label="Search"
          className="iconbtn topbar__search-button"
          onClick={() => setPaletteOpen(true)}
        >
          <Icon.search width={17} height={17} />
        </IconButton>
      )}

      {!isExternal && events.length > 0 && (
        <label className="row" style={{ gap: 7 }}>
          <span className="sr-only">Current event</span>
          <select
            className="select topbar__event-select"
            value={shownId}
            onChange={(event) => {
              // The search entry only opens the search; the choice stays as it is until an event is picked there.
              if (event.target.value === FIND_EVENT) {
                setFindOpen(true);
                return;
              }

              chooseEvent(event.target.value || null);
            }}
          >
            {unlisted && (
              <option value={unlisted.id}>
                {unlisted.code} · {unlisted.name}
              </option>
            )}
            {events.map((option) => (
              <option key={option.id} value={option.id}>
                {option.code} · {option.name}
              </option>
            ))}
            {hasMore && <option value={FIND_EVENT}>Find another event…</option>}
          </select>
        </label>
      )}

      <span className="topbar__spacer" />

      {!isExternal && (
        <>
          <button
            className="btn btn--sm"
            style={{ borderColor: "var(--red-edge)", color: "var(--red-bright)" }}
            onClick={() => navigate("/ai")}
            aria-label="AI assistant"
          >
            <Icon.sparkle />
            <span className="topbar__ai-label">AI</span>
          </button>

          <IconButton
            label="Notifications"
            badge={unread?.count ?? 0}
            onClick={() => navigate("/alerts")}
          >
            <Icon.bell width={17} height={17} />
          </IconButton>
        </>
      )}

      <div ref={menuRef} style={{ position: "relative" }}>
        <button
          className="topbar__user"
          onClick={() => setMenuOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`Account menu for ${user?.displayName ?? "you"}`}
        >
          <Avatar name={user?.displayName} src={user?.avatarUrl} size="md" />
          <span className="topbar__user-text" style={{ textAlign: "start" }}>
            <b>{user?.displayName}</b>
            <span>{user?.tenant.name}</span>
          </span>
          <Icon.chevronDown width={14} height={14} />
        </button>

        {menuOpen && (
          <div
            role="menu"
            style={{
              position: "absolute",
              insetInlineEnd: 0,
              top: "calc(100% + 8px)",
              width: 226,
              maxWidth: "calc(100vw - 24px)",
              background: "var(--ink-800)",
              border: "1px solid var(--line-strong)",
              borderRadius: "var(--r-lg)",
              boxShadow: "var(--shadow-pop)",
              padding: 6,
              zIndex: 70,
            }}
          >
            <div style={{ padding: "9px 11px 10px" }}>
              <b style={{ display: "block", font: "600 13px/1.3 var(--font)" }}>
                {user?.displayName}
              </b>
              <span className="muted" style={{ font: "var(--t-small)" }}>
                {user?.email}
              </span>
              {user?.jobTitle && (
                <span
                  className="muted"
                  style={{ display: "block", font: "var(--t-small)" }}
                >
                  {user.jobTitle}
                </span>
              )}
            </div>
            <div className="divider" style={{ margin: "2px 0 4px" }} />
            <button
              className="navlink"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                navigate("/account");
              }}
            >
              <Icon.user className="navlink__icon" />
              My account
            </button>
            <button
              className="navlink"
              role="menuitem"
              onClick={() => {
                setMenuOpen(false);
                void signOut();
              }}
            >
              <Icon.lock className="navlink__icon" />
              Sign out
            </button>
          </div>
        )}
      </div>

      {paletteOpen && !isExternal && <CommandPalette onClose={() => setPaletteOpen(false)} />}

      {findOpen && !isExternal && (
        <FindEventDialog
          onPick={(eventId) => {
            setFindOpen(false);
            chooseEvent(eventId);
          }}
          onClose={() => setFindOpen(false)}
        />
      )}
    </header>
  );
}

/**
 * Finds any event this person can reach, including those beyond the first page the switcher lists. The server
 * searches within the same reach as the list, so nothing outside it is offered.
 */
function FindEventDialog({
  onPick,
  onClose,
}: {
  onPick: (eventId: string) => void;
  onClose: () => void;
}) {
  const { user } = useSession();
  const [search, setSearch] = useState("");
  const [term, setTerm] = useState("");

  useEffect(() => {
    const id = setTimeout(() => setTerm(search.trim()), 180);
    return () => clearTimeout(id);
  }, [search]);

  // Keyed by the person, like the switcher's own list, and in the same order.
  const results = useQuery({
    queryKey: ["event-options", user?.id, "search", term],
    queryFn: () =>
      api.get<PagedResult<EventOption>>("/events", {
        page: 1,
        pageSize: 20,
        search: term,
        sortBy: "startDate",
        sortDescending: true,
      }),
    enabled: term.length >= 2,
  });

  const searching = term.length >= 2;
  const items = results.data?.items ?? [];
  const total = results.data?.totalCount ?? 0;

  return (
    <Modal
      title="Find an event"
      description="Search every event you can reach by name, code or city."
      onClose={onClose}
    >
      <input
        className="input"
        type="search"
        placeholder="Event name, code or city"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        aria-label="Search events"
      />

      <div className="stack" style={{ gap: 2, marginTop: 10 }}>
        {!searching && (
          <p className="muted" style={{ font: "var(--t-small)" }}>
            Type at least two characters.
          </p>
        )}
        {searching && results.error && (
          <ErrorState error={results.error} onRetry={() => void results.refetch()} />
        )}
        {searching && results.isLoading && <span className="spinner" />}
        {searching && results.isSuccess && items.length === 0 && (
          <p className="muted" style={{ font: "var(--t-small)" }}>
            Nothing matched “{term}”.
          </p>
        )}
        {items.map((option) => (
          <button
            key={option.id}
            type="button"
            className="navlink"
            style={{ alignItems: "flex-start", padding: "9px 11px" }}
            onClick={() => onPick(option.id)}
          >
            <Icon.calendar className="navlink__icon" style={{ marginTop: 2 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{ display: "block", font: "600 13px/1.35 var(--font)", color: "var(--text)" }}
              >
                {option.code} · {option.name}
              </span>
              {(option.clientName || option.city) && (
                <span className="muted truncate" style={{ display: "block", font: "var(--t-small)" }}>
                  {[option.clientName, option.city].filter(Boolean).join(" · ")}
                </span>
              )}
            </span>
            <Pill tone={statusTone(option.status)}>{humanise(option.status)}</Pill>
          </button>
        ))}
        {total > items.length && (
          <p className="muted" style={{ font: "var(--t-small)", padding: "6px 11px 0" }}>
            Showing {number(items.length)} of {number(total)} matches. Type more to narrow them.
          </p>
        )}
      </div>
    </Modal>
  );
}
